import { z } from "zod";
//#region src/dp-trace-sampling.ts
/**
* The write and the read of one data plane's trace sampling rate. The write comes from plan
* 2026-10-23-dp-trace-sampling (D2, D3, D5, D6, D9). The read comes from plan
* 2026-10-26-dp-trace-sampling-tools (D3, D7).
*
* Two services serve `PUT /v1/admin/data-planes/:id/observability-config/trace-sampling`:
*
* - site-admin-api serves the path to the console and the CLI. It checks
*   `cp:observability:configure-sampling`, forwards the call with the caller's bearer token, and
*   writes the CP audit row `data_plane.trace_sampling_set` from the answer.
* - release-engine serves the same path inside the mesh. It checks the same permission, answers
*   404 for a data plane id that no data plane has, and upserts the row of
*   `release.dp_observability_configs` in one transaction.
*
* Both services bind {@link dpTraceSamplingBodySchema} with `@Body({ schema })` under the global
* `ZionStrictValidationPipe`. So a body that fails it is a 400 before a handler runs, and the 400
* writes no audit row and no table row. Both answer {@link dpTraceSamplingResponseSchema}.
*
* The data plane id comes from the path. The body carries the rate alone.
*
* site-admin-api alone serves `GET /v1/admin/data-planes/:id/observability-config/trace-sampling`.
* It checks the permission of the write and answers {@link dpTraceSamplingReadResponseSchema}.
*
* The module holds data only. It has no side effect, and its one run-time import is zod.
*/
/**
* The `PUT` body: the trace sampling rate for one data plane.
*
* Who reads it: the site-admin-api handler and the release-engine handler, each through
* `@Body({ schema })`.
*
* Input: a JSON object with the one key `traceSamplingRate`. The rate is a fraction of traces
* that the data plane collector keeps:
*
* - `0` keeps no trace;
* - `1` keeps every trace, the default of every place that holds a default (D1);
* - any finite number between them, a sub-percent rate included. The schema rounds nothing. The
*   operator program passes `rate * 100` to the collector at 15 significant digits (D7, R-012).
*
* Output: the same object, unchanged. The schema has no transform, so `createOpenApiDocument()`
* renders it.
*
* Refusals, each a failed parse and so a 400:
*
* - a rate below 0 or above 1, NaN or an infinity;
* - a rate of any type other than a number, with no coercion of a numeric string;
* - an object with no `traceSamplingRate`;
* - any other key, the four other columns of the config row included (D6). They have no owner
*   ruling and no bounds.
* - any value that is not an object.
*/
const dpTraceSamplingBodySchema = z.object({ traceSamplingRate: z.number().min(0).max(1) }).strict();
/**
* The `PUT` answer of both services: the data plane and its rate before and after the write.
*
* Who writes it: release-engine, from the row it read and wrote under one transaction.
* site-admin-api parses the release-engine answer with this schema, writes the audit row from
* `before` and `after`, and returns the same object to the caller.
*
* Fields:
*
* - `dataPlaneId`: the data plane id from the path, any 8-4-4-4-12 hex string (`z.guid()`, R-002).
* - `before`: the rate that the row held before the write, a finite number with no bound (R-004):
*   the column has no check constraint. It is `null` when the data plane had no row, so the write
*   inserted the first one. The key is always present.
* - `after`: the rate that the row holds after the write, from 0 to 1. It equals the body's
*   `traceSamplingRate`.
*
* A write of the rate that the row already holds is a success with `before` equal to `after`.
*
* Refusals, each a failed parse: a missing key, an extra key, a `null` `after`, an `after` outside
* 0 to 1, NaN, and a `dataPlaneId` that is not an 8-4-4-4-12 hex string.
*/
const dpTraceSamplingResponseSchema = z.object({
	dataPlaneId: z.guid(),
	before: z.number().nullable(),
	after: z.number().min(0).max(1)
}).strict();
/**
* The answer of `GET /v1/admin/data-planes/:id/observability-config/trace-sampling` on
* site-admin-api. It holds the stored trace sampling rate of one data plane (plan
* 2026-10-26-dp-trace-sampling-tools, D3 and D7).
*
* Who writes it: `DpObservabilityConfigService.getTraceSampling` in site-admin-api, for the route
* and for the MCP tool `get_data_plane_trace_sampling`. Both check
* `cp:observability:configure-sampling`, the permission of the write. Who reads it:
* `FetchCpClient.getDataPlaneTraceSampling` in `@zion/sdk-node`, and so `zion-ops dp
* trace-sampling get` and the y/N question of `zion-ops dp trace-sampling set`.
*
* Fields:
*
* - `dataPlaneId`: the path id, any 8-4-4-4-12 hex string (`z.guid()`).
* - `traceSamplingRate`: the stored rate, from 0 to 1. A data plane with no config row reads the
*   default 1, every trace (plan 2026-10-23-dp-trace-sampling, D1). The route has no 404.
*
* Refusals, each a failed parse:
*
* - a missing key or an extra key;
* - a rate outside 0 to 1, or NaN;
* - a rate of any type other than a number;
* - a `dataPlaneId` that is not 8-4-4-4-12 hex.
*
* The schema has no transform, so `z.toJSONSchema` renders it.
*/
const dpTraceSamplingReadResponseSchema = z.object({
	dataPlaneId: z.guid(),
	traceSamplingRate: z.number().min(0).max(1)
}).strict();
//#endregion
export { dpTraceSamplingBodySchema, dpTraceSamplingReadResponseSchema, dpTraceSamplingResponseSchema };

//# sourceMappingURL=dp-trace-sampling.js.map